import { useCallback, useMemo } from 'react'

import Alert from '@web/components/Modal/Alert'
import { useModal } from '@web/components/Modal/useModal'
import { FollowButton_UserFragment } from '@web/gql/graphql'
import useFollowMethods from '@web/hooks/useFollowMethods'
import { useGoTo } from '@web/hooks/useGoTo'
import useMe from '@web/hooks/useMe'
import nClicks from '@web/libs/nlog/click/codes'
import { browserStorage } from '@web/libs/utils'

const modalMessages = {
  CANCEL_FOLLOW: '팔로우를 해제하시겠습니까?',
  CANCEL_FOLLOW_REQUEST: '팔로우 요청을 취소하시겠습니까?',
}

enum FollowButtonType {
  FOLLOW = '팔로우',
  REQUESTED = '신청됨',
  FOLLOWING = '팔로잉',
}

export const useFollow = (
  user: FollowButton_UserFragment,
  nClickCodes?: {
    followRequest?: string
    cancelFollowReqeust?: string
    cancelFollow?: string
  },
) => {
  const { me } = useMe()
  const { goToLogin } = useGoTo()
  const { followRequest, cancelFollow, cancelFollowRequest, loading } = useFollowMethods()

  const handleFollowRequest = useCallback(() => followRequest(user), [followRequest, user])

  const { openModal: openCancelFollowRequestModal } = useModal(Alert, {
    label: {
      cancel: '아니요',
      confirm: '네, 취소할래요',
    },
    callback: {
      confirm: () => cancelFollowRequest(user),
    },
    title: user.profile.nickname,
    message: modalMessages.CANCEL_FOLLOW_REQUEST,
    clickCodes: {
      cancel: nClicks.followPopup.REQCANCELNO,
      confirm: nClicks.followPopup.REQCANCELOK,
    },
  })

  const { openModal: openFirstFollowModal } = useModal(Alert, {
    label: {
      confirm: '확인',
      cancel: '취소',
    },
    callback: {
      confirm: () => handleFollowRequest(),
    },
    title: (
      <>
        팔로우를 처음 이용하시네요!
        <br />
        팔로우 기능에 대해 확인해 주세요.
      </>
    ),
    subMessage: (
      <>
        내가 팔로우한 사람이 쓴 음식점, 카페, 나들이/여행 가볼 만한 곳의 최신 리뷰를 MY플레이스에서 받아 보실 수
        있습니다.
        <br />
        또한, 팔로우한 사람(팔로잉)과 나를 팔로우한 사람(팔로워) 목록은 누구나 볼 수 있게 공개됩니다.
        <br />
        <br />
        확인을 누르면 팔로우가 진행됩니다.
      </>
    ),
    hasUnderline: true,
  })

  const { openModal: openCancelFollowModal } = useModal(Alert, {
    label: {
      cancel: '취소',
      confirm: '팔로우 해제',
    },
    callback: {
      confirm: () => cancelFollow(user),
    },
    title: user.profile.nickname,
    message: modalMessages.CANCEL_FOLLOW,
    clickCodes: {
      cancel: nClicks.followPopup.UNFOLNO,
      confirm: nClicks.followPopup.UNFOLOK,
    },
  })

  const {
    handleClick: handleFollowClick,
    type,
    nClickCode,
  } = useMemo<{
    type: FollowButtonType
    handleClick: () => void
    nClickCode?: string
  }>(() => {
    if (me === null)
      return {
        type: FollowButtonType.FOLLOW,
        handleClick: () => goToLogin({ replace: true }),
      }

    if (user.isFriend)
      return {
        type: FollowButtonType.FOLLOWING,
        handleClick: () => openCancelFollowModal(),
        nClickCode: nClickCodes?.cancelFollow,
      }

    if (user.didIRequestFollow)
      return {
        type: FollowButtonType.REQUESTED,
        handleClick: () => openCancelFollowRequestModal(),
        nClickCode: nClickCodes?.cancelFollowReqeust,
      }

    return {
      type: FollowButtonType.FOLLOW,
      handleClick: () => {
        const welcomeModalStorage = browserStorage('usedFollowing', { userId: me!.id })
        if (!welcomeModalStorage.get() && me?.stat.followingCount === 0) {
          openFirstFollowModal()
        } else {
          handleFollowRequest()
        }
        welcomeModalStorage.set('done')
      },
      nClickCode: nClickCodes?.followRequest,
    }
  }, [
    user,
    handleFollowRequest,
    openCancelFollowModal,
    openCancelFollowRequestModal,
    me,
    openFirstFollowModal,
    nClickCodes,
    goToLogin,
  ])

  return { loading, handleFollowClick, type, nClickCode }
}
